// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getPageLinkByNodeId } from "@insite/client-framework/Store/Links/LinksSelectors";
import { reducers } from "@insite/client-framework/Store/Reducers";
import { PageModel } from "@insite/client-framework/Types/PageProps";
import { Draft } from "immer";


export interface xPagesContentState {
    [key: string]: {
        data: PageModel[];
        isLoading: boolean;
        failedMessage?: string;
        articlesPagesMap: { [key: string]: PageModel };
        tagMap: Map<string, string[]>;
    };
}

const initialState: xPagesContentState = {};

const reducer = {
    "xPagesContent/BeginLoadPagesContent": (draft: Draft<xPagesContentState>, action: { key: string }) => {
        draft[action.key] = {
            isLoading: true,
            failedMessage: "",
            data: [],
            articlesPagesMap: {},
            tagMap: new Map(),
        };
    },
    "xPagesContent/FailedToLoadPagesContent": (
        draft: Draft<xPagesContentState>,
        action: { failedMessage?: string; key: string },
    ) => {
        draft[action.key] = {
            isLoading: false,
            data: [],
            failedMessage: action.failedMessage,
            articlesPagesMap: {},
            tagMap: new Map(),
        };
    },
    "xPagesContent/CompleteLoadPagesContent": (draft: Draft<xPagesContentState>, action: { data: PageModel[]; key: string; isMainRoot: boolean; state: ApplicationState }) => {
        const articlesPagesMap: { [key: string]: PageModel } = {};
        const tagMap: Map<string, string[]> = new Map();

        if (action.data) {
            action.data.forEach(page => {
                const url = getPageLinkByNodeId(action.state, page.nodeId)?.url;
                // inject url
                page.generalFields = { ...page.generalFields, url };
                if (action.isMainRoot && page.generalFields.isArticlePage) {
                    // Store the article page by nodeId
                    articlesPagesMap[page.nodeId] = page;

                    // Update tags in tagMap
                    page.generalFields.articleTags.forEach((tagItem: { fields: { tag: any; }; }) => {
                        const tagIdentifier = tagItem.fields.tag;

                        if (tagIdentifier) {
                            const existingNodes = tagMap.get(tagIdentifier) || [];
                            tagMap.set(tagIdentifier, [...existingNodes, page.nodeId]);
                        }
                    });
                }
            });
        }

        // Update the draft with the final state
        draft[action.key] = {
            isLoading: false,
            data: action.data,
            failedMessage: "",
            tagMap,
            articlesPagesMap,
        };
    },
};

const xPagesContent = createTypedReducerWithImmer(initialState, reducer);
(reducers as any).xPagesContent = xPagesContent;

export type xPagesContent = {
    xPagesContent: xPagesContentState;
} & ApplicationState;

type xPagesContentReducers = {
    [key: string]: typeof xPagesContent;
};

export type xPagesContentActions = Parameters<xPagesContentReducers[keyof xPagesContentReducers]>[1];
